import React, { forwardRef } from 'react';
import { Sun } from 'lucide-react';

const OrderInvoice = forwardRef(({ order, isVendor = false, vendorName = '' }, ref) => {
    if (!order) return null;

    // Formatting helpers
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Customer formatting safely
    const customerName = order.customer?.name || 'Valued Customer';
    const customerEmail = order.customer?.email || 'N/A';
    const customerPhone = order.customer?.phone || 'N/A';
    
    // Address format
    let addressStr = order.shipping_address;
    if (typeof order.shipping_address === 'object' && order.shipping_address !== null) {
        const ad = order.shipping_address;
        addressStr = `${ad.address}, ${ad.city}, ${ad.state} - ${ad.pincode}`;
    }

    const subtotal = order.total_amount || 0;
    const isVendorPricing = subtotal === 0; // If total is 0, it might be manual pricing. We display N/A.
    const gst = Math.round(subtotal * 0.18);
    const grandTotal = subtotal + gst;

    return (
        <div ref={ref} className="p-10 w-full min-h-screen bg-white text-black font-sans box-border text-[13px] print:block">
            {/* Header */}
            <div className="flex justify-between items-start mb-10 border-b-2 border-gray-800 pb-6">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Sun className="w-10 h-10 text-orange-500" />
                        <h1 className="text-3xl font-bold tracking-tight">SolarSavers</h1>
                    </div>
                    <p className="text-gray-500 font-medium tracking-widest text-xs uppercase mb-1">
                        {isVendor ? 'Packing Slip' : 'Tax Invoice'}
                    </p>
                    <p className="text-gray-600">contact@thesolarsavers.com</p>
                    <p className="text-gray-600">+91 1800-SOLAR-NOW</p>
                </div>
                <div className="text-right">
                    <h2 className="text-4xl font-light text-gray-300 mb-2 uppercase tracking-wider">
                        {isVendor ? 'Slip' : 'Invoice'}
                    </h2>
                    <p className="font-semibold text-lg text-gray-800">{order.id}</p>
                    <p className="text-gray-500 mt-1">Date: {formatDate(order.created_at)}</p>
                    {(order.assigned_vendor_name || vendorName) && (
                        <p className="text-gray-500 mt-1">Vendor: <span className="font-semibold text-gray-700">{order.assigned_vendor_name || vendorName}</span></p>
                    )}
                </div>
            </div>

            {/* Bill To & Ship To */}
            <div className="flex justify-between mb-10">
                <div className="w-1/2 pr-4">
                    <h3 className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-3">Bill To</h3>
                    <p className="font-bold text-base mb-1">{customerName}</p>
                    <p className="text-gray-600 mb-1">{customerEmail}</p>
                    <p className="text-gray-600">{customerPhone}</p>
                </div>
                <div className="w-1/2 pl-4">
                    <h3 className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-3">Ship To / Deploy Location</h3>
                    <p className="text-gray-800 font-medium leading-relaxed max-w-[80%]">
                        {addressStr || 'No shipping address provided'}
                    </p>
                </div>
            </div>

            {/* Tracking (if shipped) */}
            {order.tracking_number && (
                <div className="mb-10 bg-gray-50 p-4 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-4">
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">Carrier</p>
                            <p className="font-medium">{order.carrier || 'N/A'}</p>
                        </div>
                        <div className="h-8 w-px bg-gray-200"></div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">Tracking Number</p>
                            <p className="font-mono font-medium">{order.tracking_number}</p>
                        </div>
                        {order.expected_delivery && (
                            <>
                                <div className="h-8 w-px bg-gray-200"></div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">Expected Delivery</p>
                                    <p className="font-medium text-green-700">{formatDate(order.expected_delivery).split(',')[0]}</p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Items Table */}
            <div className="mb-10">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b-2 border-gray-800">
                            <th className="py-3 px-2 font-bold text-xs uppercase tracking-wider text-gray-500">Item Details</th>
                            <th className="py-3 px-2 font-bold text-xs uppercase tracking-wider text-gray-500 text-center w-24">Qty</th>
                            <th className="py-3 px-2 font-bold text-xs uppercase tracking-wider text-gray-500 text-right w-32">Rate</th>
                            <th className="py-3 px-2 font-bold text-xs uppercase tracking-wider text-gray-500 text-right w-32">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {order.items?.map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                                <td className="py-4 px-2">
                                    <p className="font-semibold text-gray-800">{item.name}</p>
                                    <p className="text-xs text-gray-500 mt-1">PID: {item.product_id}</p>
                                </td>
                                <td className="py-4 px-2 text-center font-medium">{item.quantity}</td>
                                <td className="py-4 px-2 text-right text-gray-600">
                                    {isVendorPricing ? 'TBD' : formatCurrency(item.price)}
                                </td>
                                <td className="py-4 px-2 text-right font-medium text-gray-800">
                                    {isVendorPricing ? 'TBD' : formatCurrency(item.price * item.quantity)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-16">
                <div className="w-72">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-500 font-medium">Subtotal</span>
                        <span className="font-medium">{isVendorPricing ? 'TBD' : formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-500 font-medium">Shipping</span>
                        <span className="font-medium text-green-600">Free</span>
                    </div>
                    {!isVendor && (
                        <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-500 font-medium">GST (18%)</span>
                            <span className="font-medium text-gray-600">{formatCurrency(gst)}</span>
                        </div>
                    )}
                    <div className="flex justify-between py-4 border-b-2 border-gray-800 mt-2">
                        <span className="font-bold text-lg">Total</span>
                        <span className="font-bold text-xl">{isVendorPricing ? 'TBD' : formatCurrency(isVendor ? subtotal : grandTotal)}</span>
                    </div>
                </div>
            </div>

            {/* Footer / Signature */}
            <div className="flex justify-between items-end text-sm text-gray-500">
                <div className="max-w-md">
                    <p className="font-bold mb-1 text-gray-700">Terms & Conditions</p>
                    <p className="text-xs">
                        This is a computer-generated document. No signature is required.
                        Returns are valid within 7 days of delivery as per SolarSavers standard policy.
                        Warranty claims should be directed to the respective manufacturer via vendor tracking.
                    </p>
                </div>
                <div className="text-center">
                    <div className="w-48 h-16 border-b border-gray-300 mb-2"></div>
                    <p className="font-medium uppercase tracking-wider text-xs">Authorized Signatory</p>
                </div>
            </div>
        </div>
    );
});

OrderInvoice.displayName = 'OrderInvoice';
export default OrderInvoice;
